/**
 * Console Error Interceptor
 *
 * Overrides console.error globally to capture all error logging
 * while preserving original functionality and preventing infinite loops.
 *
 * Features:
 * - Preserves original console.error output (dev tools still work)
 * - Sends errors to ErrorTracker for ClickHouse logging
 * - Infinite loop prevention with isProcessing flag
 * - Install/uninstall lifecycle management
 *
 * Usage:
 * ```typescript
 * import { consoleErrorInterceptor } from './console-error-interceptor';
 *
 * // In _app.tsx useEffect
 * consoleErrorInterceptor.install();
 * return () => consoleErrorInterceptor.uninstall();
 * ```
 */

import { errorTracker } from './error-tracking';

/**
 * Console output that is provably not ours and that no one can ever act on.
 *
 * The interceptor earns its keep by catching errors no `try` block saw. The cost
 * is that it also catches every vendor bundle's `console.error`, and a vendor
 * failing in a way our code cannot reach is a stored row nobody will ever open.
 * On 2026-08-31 a single such pattern — an ad frame reading its own iframe
 * across origins — was 225 of production's 927 error rows for the day, and it
 * had carried the standing verdict `cross-origin-iframe-read` in
 * `scripts/error-triage/noise-rules.ts` for four consecutive triage runs.
 *
 * Anything listed here is invisible to triage, so the bar is high: the browser
 * must be telling a third party "no", not merely be noisy. Keep the reason next
 * to the pattern, and match the engine's own wording rather than the vendor's
 * prose around it — the same ad bundle words its prefix in Chinese on some
 * pages and English on others, but every engine spells the security refusal in
 * English.
 */
const UNACTIONABLE_CONSOLE_PATTERNS: ReadonlyArray<{ id: string; why: string; test: (text: string) => boolean }> = [
  {
    id: 'cross-origin-iframe-read',
    why:
      'A frame tried to reach into another origin\'s document and the browser ' +
      'refused, which is the same-origin policy working. We render no such read; ' +
      'the throwing frame is always inside an ad or embed bundle we load but do ' +
      'not build, and there is no version of our code that changes the outcome.',
    test: (text) =>
      text.includes('Blocked a frame with origin') ||
      text.includes('accessing a cross-origin frame') ||
      // Firefox's wording for the same refusal.
      text.includes('cross-origin object'),
  },
  {
    id: 'widget-cmp-script-blocked',
    why:
      "The ad widget (widget.sellwild.com) logs this from the Google consent script's " +
      '`onerror`, then loads the ads anyway. The browser refused the script: an ad ' +
      'blocker, corporate DNS or a dropped mobile connection. 1,423 rows over 35 days, ' +
      'the largest cluster in production, and triage had ruled it noise since 2026-08-31.',
    test: (text) => text.includes('Google CMP script failed to load'),
  },
  {
    id: 'widget-customizations-transport-failure',
    why:
      'The ad widget fetches its own `widget.sellwild.com/<partner>/<slug>.json` and the ' +
      'browser gave up before any answer came back. A transport failure carries no ' +
      'cause, so the row can never say more. A bad answer (a JSON SyntaxError) or an ' +
      "injected script's ReferenceError does not match and is still stored.",
    test: (text) =>
      text.startsWith('Failed to load customizations') &&
      /TypeError: (?:Failed to fetch|Load failed|NetworkError when attempting to fetch resource)/.test(text),
  },
];

/**
 * Was this console.error called only by code that has no script URL?
 *
 * Every console.error row is filed with the interceptor's own frames on top, so
 * ErrorTracker's own-scripts check always sees our `_app` chunk and says "ours",
 * whoever made the call. A script the browser injects with no source URL — a
 * security suite's anti-fingerprinting shim, an extension's main-world code —
 * shows up in V8 only as `<anonymous>:line:col`. Our bundles, and inline scripts
 * in our HTML, always carry a URL. On 2026-10-14 Norton's browser filed
 * "updating iframe fingerprint properties" from `<anonymous>:409:21` as an app
 * error (cluster 939d2c6b0d3b0883).
 *
 * `stack` is a V8 stack taken inside the interceptor; its first frame is ours and
 * is skipped. Native frames (`at Array.forEach (<anonymous>)`) carry no position
 * and say nothing either way. Any other frame — a URL, a file path, an `eval at`
 * origin — means a real script is on the path, so the row is kept. Other engines
 * print frames differently and are always kept.
 */
export function isCalledOnlyByInjectedCode(stack: string | undefined): boolean {
  if (!stack) return false;
  const frames = stack
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.startsWith('at '))
    .slice(1);

  let injected = 0;
  for (const frame of frames) {
    if (/^at (?:\S+ )?\(?<anonymous>:\d+:\d+\)?$/.test(frame)) {
      injected++;
    } else if (!/^at \S+ \(<anonymous>\)$/.test(frame)) {
      return false;
    }
  }
  return injected > 0;
}

/**
 * Where the page was in its life when the console.error fired.
 *
 * A vendor's fetch that rejects with `Failed to fetch` / `Load failed` reads the
 * same whether the endpoint broke or the tab closed under it. On 2026-09-22
 * next-auth's CLIENT_FETCH_ERROR on /api/auth/session went from ~9/day to 39,
 * all signed-out, in bursts of 3-7 separate sessions loading different product
 * pages within one second from cloud IP ranges — a page loader closing each tab
 * before the session poll returned. Nothing on the row could prove that, so the
 * triage rule that calls it "aborted, not answered" was an inference. These
 * three fields make it a reading.
 */
export interface PageLifecycle {
  visibility: string;
  /** pagehide or beforeunload fired, and no pageshow has brought the page back. */
  leaving: boolean;
  msSinceLoad: number;
}

class ConsoleErrorInterceptor {
  private originalConsoleError: typeof console.error | null = null;
  private isIntercepting = false;
  private isProcessing = false; // Prevent infinite loops
  private leaving = false;
  private readonly markLeaving = (): void => {
    this.leaving = true;
  };
  // A page restored from the back/forward cache is live again.
  private readonly markReturned = (): void => {
    this.leaving = false;
  };

  /**
   * Install the console.error interceptor
   * Safe to call multiple times (no-op if already installed)
   *
   * Browser only: pages/_app.tsx installs it from an effect, which never runs
   * on the server, so window, document and performance are always there.
   */
  install(): void {
    if (this.isIntercepting) return;

    // Store original console.error
    this.originalConsoleError = console.error;

    // Override with intercepted version
    console.error = this.interceptedError.bind(this);

    window.addEventListener('pagehide', this.markLeaving);
    window.addEventListener('beforeunload', this.markLeaving);
    window.addEventListener('pageshow', this.markReturned);

    this.isIntercepting = true;
  }

  /**
   * Uninstall the console.error interceptor
   * Restores original console.error functionality
   */
  uninstall(): void {
    // install() sets originalConsoleError whenever it sets isIntercepting.
    if (!this.isIntercepting) return;

    // Restore original console.error
    console.error = this.originalConsoleError;

    window.removeEventListener('pagehide', this.markLeaving);
    window.removeEventListener('beforeunload', this.markLeaving);
    window.removeEventListener('pageshow', this.markReturned);
    this.leaving = false;

    this.isIntercepting = false;
    this.originalConsoleError = null;
  }

  /**
   * Intercepted console.error implementation
   * Calls original first, then tracks the error
   */
  private interceptedError(...args: unknown[]): void {
    // Prevent infinite loops (if error tracking calls console.error)
    if (this.isProcessing) {
      if (this.originalConsoleError) {
        this.originalConsoleError.apply(console, args);
      }
      return;
    }

    this.isProcessing = true;

    try {
      // Call original console.error first (preserve output in dev tools)
      if (this.originalConsoleError) {
        this.originalConsoleError.apply(console, args);
      }

      // Track the error. The stack is taken here so its first frame is this one.
      this.trackConsoleError(args, new Error().stack);
    } catch (err) {
      // Silent failure - never break the app due to error tracking
      // Use original console.error if available
      if (this.originalConsoleError) {
        try {
          this.originalConsoleError.call(console, '[ConsoleErrorInterceptor] Failed to track error:', err);
        } catch {
          // Give up silently
        }
      }
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Convert console.error arguments to Error object and track
   */
  private trackConsoleError(args: unknown[], callerStack?: string): void {
    // Skip React warnings - these are dev-time messages, not real errors
    const firstArg = args[0];
    if (typeof firstArg === 'string' && firstArg.startsWith('Warning: ')) {
      return;
    }

    // Third-party output the browser itself refused. Still printed above, just
    // not stored — see UNACTIONABLE_CONSOLE_PATTERNS.
    if (this.isUnactionable(args) || this.isSignedOutSessionPollFailure(args)) {
      return;
    }

    // No script with a URL made this call, so none of ours did.
    if (isCalledOnlyByInjectedCode(callerStack)) {
      return;
    }

    const error = this.convertArgsToError(args);

    // Track via ErrorTracker (sends to ClickHouse only - no user notifications)
    errorTracker.captureException(error, {
      component: 'console.error',
      extra: {
        originalArgs: args.map(arg => this.serializeArg(arg)),
        page: this.pageLifecycle(),
      }
    });
  }

  private pageLifecycle(): PageLifecycle {
    return {
      visibility: document.visibilityState,
      leaving: this.leaving,
      msSinceLoad: Math.round(performance.now()),
    };
  }

  /**
   * next-auth's own CLIENT_FETCH_ERROR for the `/api/auth/session` poll, in a
   * browser with no signed-in user on record.
   *
   * The endpoint answers `200 {}` to a signed-out visitor. From 2026-09-01 to
   * 10-02 this failed 448 times, never for a known user: Bingbot's renderer hands every
   * fetch an empty body (56 of 57 "Unexpected end of JSON input" rows), and
   * datacenter-proxied partner traffic loses the request in transit ("Failed to
   * fetch" / "Load failed"). A visitor with no session loses nothing when the
   * check fails. Triage had relabelled this family 30 ways over 12 branches.
   *
   * Still stored when a signed-in user hits it, because then a failed poll can
   * show them signed out, and for every other next-auth call (csrf, providers,
   * sign-in), which sit on the login path.
   */
  private isSignedOutSessionPollFailure(args: unknown[]): boolean {
    const [first] = args;
    if (typeof first !== 'string' || !first.startsWith('[next-auth][error][CLIENT_FETCH_ERROR]')) {
      return false;
    }
    const isSessionPoll = args.some((arg) => {
      const url = arg && typeof arg === 'object' ? (arg as { url?: unknown }).url : undefined;
      return typeof url === 'string' && url.endsWith('/session');
    });
    return isSessionPoll && !errorTracker.hasKnownUser();
  }

  /**
   * The parts of an Event a row can carry; it cannot be JSON-stringified.
   * A load failure says only `type: error` on a SCRIPT, so the element's own
   * URL is what tells one script from another.
   */
  private describeEvent(event: Event): Record<string, unknown> {
    const target = event.target as (HTMLElement & { src?: unknown; href?: unknown }) | null;
    const eventInfo: Record<string, unknown> = {
      type: event.type,
      target: target ? target.tagName || 'unknown' : 'null',
    };
    const url = target?.src || target?.href;
    if (typeof url === 'string' && url) eventInfo.src = url.slice(0, 200);
    if ('message' in event) eventInfo.message = (event as ErrorEvent).message;
    if ('filename' in event) eventInfo.filename = (event as ErrorEvent).filename;
    if ('lineno' in event) eventInfo.lineno = (event as ErrorEvent).lineno;
    if ('colno' in event) eventInfo.colno = (event as ErrorEvent).colno;
    return eventInfo;
  }

  /**
   * Does this call match a standing "not ours, not fixable" pattern?
   */
  private isUnactionable(args: unknown[]): boolean {
    const text = this.filterText(args);
    if (!text) return false;
    return UNACTIONABLE_CONSOLE_PATTERNS.some((pattern) => pattern.test(text));
  }

  /**
   * The text a filter pattern gets to look at.
   *
   * Deliberately not `stringifyArg`: the interesting argument is usually a
   * `DOMException`, which is not an `instanceof Error` in every engine and
   * JSON-stringifies to `{}`. Reading `name`/`message` off any object catches
   * `Error`, `DOMException` and the plain object a vendor logs instead.
   */
  private filterText(args: unknown[]): string {
    return args
      .map((arg) => {
        if (typeof arg === 'string') return arg;
        if (arg && typeof arg === 'object') {
          const { name, message } = arg as { name?: unknown; message?: unknown };
          if (typeof message === 'string') {
            return typeof name === 'string' ? `${name}: ${message}` : message;
          }
        }
        return '';
      })
      .join(' ');
  }

  /**
   * Convert console.error arguments to Error object
   */
  private convertArgsToError(args: unknown[]): Error {
    if (args.length === 0) {
      return new Error('console.error called with no arguments');
    }

    // If first arg is already an Error, use it
    if (args[0] instanceof Error) {
      return args[0];
    }

    // Otherwise, create Error from string representation
    const message = args.map(arg => this.stringifyArg(arg)).join(' ');
    const error = new Error(message);

    // Set name to distinguish console.error from thrown errors
    error.name = 'ConsoleError';

    return error;
  }

  /**
   * Convert argument to string for error message
   */
  private stringifyArg(arg: unknown): string {
    if (arg === null) return 'null';
    if (arg === undefined) return 'undefined';
    if (typeof arg === 'string') return arg;
    if (typeof arg === 'number' || typeof arg === 'boolean') return String(arg);
    if (arg instanceof Error) return `${arg.name}: ${arg.message}`;

    // Handle Event objects (which can't be JSON.stringify'd due to circular refs)
    if (typeof Event !== 'undefined' && arg instanceof Event) {
      return `Event(${JSON.stringify(this.describeEvent(arg))})`;
    }

    try {
      return JSON.stringify(arg);
    } catch {
      // For objects that can't be stringified, try to extract useful info
      if (typeof arg === 'object' && (arg as { constructor?: { name?: string } }).constructor?.name) {
        return `[${(arg as { constructor: { name: string } }).constructor.name}]`;
      }
      return '[object]';
    }
  }

  /**
   * Serialize argument for extra context (handles circular references)
   */
  private serializeArg(arg: unknown): unknown {
    if (arg === null || arg === undefined) return arg;
    if (typeof arg === 'string' || typeof arg === 'number' || typeof arg === 'boolean') return arg;

    if (arg instanceof Error) {
      return {
        name: arg.name,
        message: arg.message,
        stack: arg.stack
      };
    }

    // Handle Event objects (which can't be JSON.stringify'd due to circular refs)
    if (typeof Event !== 'undefined' && arg instanceof Event) {
      return this.describeEvent(arg);
    }

    // For objects, try JSON with circular reference handling
    try {
      JSON.stringify(arg);
      return arg;
    } catch {
      // Circular reference detected, return constructor name
      if (typeof arg === 'object' && (arg as { constructor?: { name?: string } }).constructor?.name) {
        return `[${(arg as { constructor: { name: string } }).constructor.name}]`;
      }
      return '[object]';
    }
  }
}

// Export singleton instance
export const consoleErrorInterceptor = new ConsoleErrorInterceptor();

// Export class for testing
export { ConsoleErrorInterceptor };
